import type { ChemistryProfiles } from "@repo/shared/api";
import type { Chemistry } from "@repo/shared/types";

import { Manager } from "../redux/squadBuilder/squadBuilderUtils";

export type { ChemistryProfiles };

export const HERO_CLUB_IDS = [114605, 132794]; // hero, Hall of FUT
export const ICON_CLUB_ID = 112658;
export const ICON_LEAGUE_ID = 2118;

export type ChemPlayer = {
  positionIndex: number;
  countryId: number;
  clubId: number;
  leagueId: number;
  possiblePositions: string[];
  versionId: number;
};

type Props = {
  players: ChemPlayer[];
  formationPositions: string[];
  chemistryProfiles?: ChemistryProfiles;
  manager?: Manager;
  clubLinks?: Record<number, number>;
};

export type PlayersChemistry = {
  positionIndex: number;
  chemistry: Chemistry;
};
export type ChemistryObject = {
  playersChemistry: PlayersChemistry;
  countryCounter: { [key: string]: number };
  leagueCounter: { [key: string]: number };
  clubCounter: { [key: string]: number };
  playersOutOfPosition: ChemPlayer[];
};

export const countryCountToChem = (countryCount: number): Chemistry => {
  if (countryCount < 2) return 0;
  else if (countryCount < 5) return 1;
  else if (countryCount < 8) return 2;
  return 3;
};

export const leagueCountToChem = (leagueCount: number): Chemistry => {
  if (leagueCount < 3) return 0;
  else if (leagueCount < 5) return 1;
  else if (leagueCount < 8) return 2;
  return 3;
};

export const clubCountToChem = (clubCount: number): Chemistry => {
  if (clubCount < 2) return 0;
  else if (clubCount < 4) return 1;
  else if (clubCount < 7) return 2;
  return 3;
};

export const calculateChemistry = ({
  players,
  formationPositions,
  manager,
  chemistryProfiles,
  clubLinks,
}: Props): ChemistryObject => {
  let iconCounter = 0;
  let countryCounter = {};
  let leagueCounter = {};
  let clubCounter = {};
  let playersOutOfPosition = [];

  players.forEach((player) => {
    if (
      !player.possiblePositions?.includes(
        formationPositions[player.positionIndex],
      )
    ) {
      playersOutOfPosition.push(player);
      return;
    }
    if (
      player.clubId === ICON_CLUB_ID ||
      chemistryProfiles.leagueBonusUniversal.includes(player.versionId)
    ) {
      iconCounter += 1;
    }

    const countryBonus =
      chemistryProfiles["countryBonuses"][player.versionId] || 1;

    if (player.countryId in countryCounter) {
      countryCounter[player.countryId] += countryBonus;
    } else {
      countryCounter[player.countryId] = countryBonus;
    }
    const leagueBonus =
      player.clubId === ICON_CLUB_ID ||
      chemistryProfiles.leagueBonusUniversal.includes(player.versionId)
        ? 0
        : chemistryProfiles["leagueBonuses"][player.versionId] || 1;

    if (player.leagueId in leagueCounter) {
      leagueCounter[player.leagueId] += leagueBonus;
    } else {
      leagueCounter[player.leagueId] = leagueBonus;
    }
    const clubBonus =
      HERO_CLUB_IDS.includes(player.clubId) || player.clubId === ICON_CLUB_ID
        ? 0
        : chemistryProfiles["clubBonuses"][player.versionId] || 1;

    const clubId = clubLinks?.[player.clubId] ?? player.clubId;
    if (clubId in clubCounter) {
      clubCounter[clubId] += clubBonus;
    } else {
      clubCounter[clubId] = clubBonus;
    }
  });

  if (manager) {
    if (manager.country) {
      if (countryCounter[manager.country]) {
        countryCounter[manager.country] += 1;
      } else {
        countryCounter[manager.country] = 1;
      }
    }
    if (manager.league) {
      if (leagueCounter[manager.league]) {
        leagueCounter[manager.league] += 1;
      } else {
        leagueCounter[manager.league] = 1;
      }
    }
  }
  Object.entries(leagueCounter).forEach((element) => {
    leagueCounter[element[0]] += iconCounter;
  });

  const playersChemistry = {};

  players.forEach((player) => {
    if (
      !player.possiblePositions.includes(
        formationPositions[player.positionIndex],
      )
    ) {
      playersChemistry[player.positionIndex] = 0;
      return;
    }
    if (
      player.clubId === ICON_CLUB_ID ||
      HERO_CLUB_IDS.includes(player.clubId) ||
      chemistryProfiles.playerFullChem.includes(player.versionId)
    ) {
      playersChemistry[player.positionIndex] = 3;
      return;
    }

    const clubId = clubLinks?.[player.clubId] ?? player.clubId;
    let playerChemistry: Chemistry = 0;
    playerChemistry += countryCountToChem(countryCounter[player.countryId]);
    playerChemistry += leagueCountToChem(leagueCounter[player.leagueId]);
    playerChemistry += clubCountToChem(clubCounter[clubId]);

    playersChemistry[player.positionIndex] =
      playerChemistry <= 3 ? (playerChemistry as Chemistry) : 3;
  });

  return {
    playersChemistry: playersChemistry as PlayersChemistry,
    countryCounter,
    leagueCounter,
    clubCounter,
    playersOutOfPosition,
  };
};
